import { SEARCH_TYPE_LABELS, type SearchHit } from '@whn/shared';
import {
  Building2,
  CalendarCheck,
  Contact,
  Handshake,
  Loader2,
  Search,
  Sparkles,
  Boxes,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useSearch } from './use-search';

const ICONS: Record<SearchHit['type'], LucideIcon> = {
  LEAD: Sparkles,
  ACCOUNT: Building2,
  CONTACT: Contact,
  DEAL: Handshake,
  ACTIVITY: CalendarCheck,
  // Every custom module shares one icon here. The palette shows the module's
  // own label as the subtitle, which is what tells them apart; a per-module
  // icon would mean resolving the module list to draw a search result.
  CUSTOM: Boxes,
};

/** Long enough that a fast typist sends one request, short enough to feel live. */
const DEBOUNCE_MS = 180;

/**
 * Global search, as a command palette.
 *
 * Built by hand on the ARIA combobox pattern rather than on a dialog with a
 * list inside it. The distinction matters for screen readers: focus stays in
 * the text field the whole time, `aria-activedescendant` moves the *virtual*
 * cursor through the options, and each move is announced. A dialog that moves
 * real focus to a list item leaves the typed query behind and reads as a
 * different control on every arrow press.
 */
export function CommandPalette({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const [term, setTerm] = React.useState('');
  const [debounced, setDebounced] = React.useState('');
  const [activeIndex, setActiveIndex] = React.useState(0);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLUListElement>(null);

  const results = useSearch(debounced);
  const hits = results.data?.hits ?? [];

  React.useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(term), DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [term]);

  // A fresh query starts at the top; leaving the cursor on index 4 of the
  // previous results means Enter opens something the user never saw.
  React.useEffect(() => {
    setActiveIndex(0);
  }, [debounced]);

  React.useEffect(() => {
    if (open) {
      setTerm('');
      setDebounced('');
      setActiveIndex(0);
      // The input mounts with the dialog, so focus has to wait a frame.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // Keep the virtual cursor in view when it moves past the fold.
  React.useEffect(() => {
    listRef.current?.children[activeIndex]?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  if (!open) return null;

  const go = (hit: SearchHit | undefined): void => {
    if (!hit) return;
    onOpenChange(false);
    navigate(hit.href);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        // Wraps, so holding one arrow key reaches every option.
        setActiveIndex((current) => (hits.length === 0 ? 0 : (current + 1) % hits.length));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex((current) =>
          hits.length === 0 ? 0 : (current - 1 + hits.length) % hits.length,
        );
        break;
      case 'Home':
        event.preventDefault();
        setActiveIndex(0);
        break;
      case 'End':
        event.preventDefault();
        setActiveIndex(Math.max(hits.length - 1, 0));
        break;
      case 'Enter':
        event.preventDefault();
        go(hits[activeIndex]);
        break;
      case 'Escape':
        event.preventDefault();
        onOpenChange(false);
        break;
      default:
        break;
    }
  };

  const status = describeStatus(debounced, results.isFetching, hits.length, results.isError);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-[10vh] backdrop-blur-[1px]"
      // Clicking the backdrop closes; clicks inside the panel stop here.
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onOpenChange(false);
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Search records"
        className="w-full max-w-xl overflow-hidden rounded-card border border-border-subtle bg-surface-raised shadow-xl"
      >
        <div className="flex items-center gap-3 border-b border-border-subtle px-4">
          {results.isFetching ? (
            <Loader2 className="size-4 shrink-0 animate-spin text-ink-subtle" aria-hidden="true" />
          ) : (
            <Search className="size-4 shrink-0 text-ink-subtle" aria-hidden="true" />
          )}
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded={hits.length > 0}
            aria-controls="command-palette-results"
            aria-activedescendant={hits.length > 0 ? `command-hit-${activeIndex}` : undefined}
            aria-autocomplete="list"
            aria-label="Search leads, accounts, contacts, deals and activities"
            autoComplete="off"
            placeholder="Search records…"
            className="h-12 w-full bg-transparent text-sm text-ink outline-none placeholder:text-ink-subtle"
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            onKeyDown={onKeyDown}
          />
          <kbd className="shrink-0 rounded border border-border-subtle px-1.5 py-0.5 text-[10px] font-medium text-ink-subtle">
            Esc
          </kbd>
        </div>

        {hits.length > 0 ? (
          <ul
            ref={listRef}
            id="command-palette-results"
            role="listbox"
            aria-label="Search results"
            className="max-h-80 overflow-y-auto p-1.5"
          >
            {hits.map((hit, index) => {
              const Icon = ICONS[hit.type];
              return (
                <li
                  key={`${hit.type}-${hit.id}`}
                  id={`command-hit-${index}`}
                  role="option"
                  aria-selected={index === activeIndex}
                  className={cn(
                    'flex cursor-pointer items-center gap-3 rounded-md px-3 py-2',
                    index === activeIndex ? 'bg-brand-soft' : 'hover:bg-surface-sunken',
                  )}
                  // Mouse *down*, not click: a click fires after the input
                  // blurs, and the palette would be gone by then.
                  onMouseDown={(event) => {
                    event.preventDefault();
                    go(hit);
                  }}
                  onMouseEnter={() => setActiveIndex(index)}
                >
                  <Icon
                    className={cn(
                      'size-4 shrink-0',
                      index === activeIndex ? 'text-brand-ink' : 'text-ink-subtle',
                    )}
                    aria-hidden="true"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-ink">{hit.title}</span>
                    {hit.subtitle ? (
                      <span className="block truncate text-xs text-ink-muted">{hit.subtitle}</span>
                    ) : null}
                  </span>
                  <span className="shrink-0 rounded bg-surface-sunken px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-ink-subtle">
                    {SEARCH_TYPE_LABELS[hit.type]}
                  </span>
                </li>
              );
            })}
          </ul>
        ) : null}

        {/*
          One live region for every state, so a screen reader hears "12
          results" or "nothing found" without the list itself being announced
          item by item as it changes under the cursor.
        */}
        <p
          role="status"
          aria-live="polite"
          className={cn(
            'px-4 py-3 text-xs text-ink-muted',
            hits.length > 0 && 'border-t border-border-subtle',
          )}
        >
          {status}
        </p>
      </div>
    </div>
  );
}

function describeStatus(
  term: string,
  isFetching: boolean,
  count: number,
  isError: boolean,
): string {
  if (isError) return 'Search failed. Try again.';
  if (term.trim().length < 2) return 'Type at least two characters. ↑↓ to move, Enter to open.';
  if (isFetching && count === 0) return 'Searching…';
  if (count === 0) return `Nothing matches “${term}”.`;
  return `${count} result${count === 1 ? '' : 's'}. ↑↓ to move, Enter to open.`;
}
